import type { Connection } from './network';
import type { AuthClient } from './auth';
import type { Viewport } from './viewport';
import { resultSchema, type Mutation } from './content';
import { lightingPreset, lightingSchema, type LightingProfile, LIGHTING_PRESETS } from './lighting-profile';
import { LightingOverrides } from './lighting-overrides';

const el = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
export class LightingPanel {
  private overrides = new LightingOverrides();
  private events = new AbortController();
  private signature = '';
  private contextKey = '';
  private draft = lightingPreset('neutral');
  private busy = false;
  private pending: Mutation | null = null;
  constructor(private viewport: Viewport, private connection: Connection, private auth: AuthClient, private local: boolean, private stopInput: () => void) {
    const options = {signal:this.events.signal};
    el('lighting-reset').addEventListener('click',() => this.apply(null),options);
    el<HTMLSelectElement>('lighting-look').addEventListener('change',event => {
      const choice = (event.target as HTMLSelectElement).value;
      if (choice === 'room') { this.apply(null); return; }
      if (!Object.hasOwn(LIGHTING_PRESETS,choice)) return;
      const profile = lightingPreset(choice as keyof typeof LIGHTING_PRESETS);
      profile.lights = structuredClone(this.draft.lights); // Retain fixtures when changing the overall mood.
      if(this.draft.probes) profile.probes=structuredClone(this.draft.probes);
      this.apply(profile);
    },options);
    for (const control of document.querySelectorAll<HTMLInputElement>('[data-lighting-field]')) control.addEventListener('change',() => {
      const draft = structuredClone(this.draft), [group,field] = control.dataset.lightingField!.split('.');
      const record = draft as unknown as Record<string,unknown>, target = field ? record[group!] as Record<string,unknown> : record;
      target[field ?? group!] = control.type === 'color' ? control.value : Number(control.value);
      this.apply(draft);
    },options);
    el('lighting-apply-json').addEventListener('click',() => {
      try { this.apply(lightingSchema.parse(JSON.parse(el<HTMLTextAreaElement>('lighting-json').value))); }
      catch { el('lighting-status').textContent = 'Invalid profile. Check the lighting schema, ranges and light budgets.'; }
    },options);
    el('lighting-publish').addEventListener('click',() => void this.publish(),options);
    this.refresh();
  }
  private context() {
    const room = this.local ? undefined : this.connection.state.content?.room;
    return {room, world:this.local ? 'local' : this.auth.state?.worldId ?? 'signed-out', id:this.local ? 'local' : room?.id ?? this.connection.roomID};
  }
  private apply(profile: LightingProfile | null): void {
    try {
      this.stopInput(); const {world,id} = this.context();
      const saved = this.overrides.set(world,id,profile);
      this.signature = ''; this.refresh();
      el('lighting-status').textContent = profile ? `Local lighting applied${saved ? ' and remembered for this room' : ' for this session'}. Other visitors are unaffected.` : 'Following the room lighting.';
    } catch { el('lighting-status').textContent = 'Invalid lighting value. Check ranges; fog start must be before fog end.'; }
  }
  refresh(): void {
    const {room,world,id} = this.context(), context = `${world}:${id}:${this.auth.state?.csrf ?? ''}`;
    if (context !== this.contextKey) { this.contextKey = context; this.pending = null; this.busy = false; this.signature = ''; el('lighting-status').textContent = ''; }
    const override = this.overrides.get(world,id), profile = override ?? room?.settings.lighting;
    this.viewport.setLightingProfile(profile,override ? 'local' : 'room');
    const signature = JSON.stringify([context,!!override,profile]);
    if (signature !== this.signature) {
      this.signature = signature; this.draft = structuredClone(profile ?? lightingPreset('neutral'));
      el<HTMLSelectElement>('lighting-look').value = override ? 'custom' : 'room';
      for (const control of document.querySelectorAll<HTMLInputElement>('[data-lighting-field]')) {
        const [group,field] = control.dataset.lightingField!.split('.'), record = this.draft as unknown as Record<string,unknown>;
        control.value = String(field ? (record[group!] as Record<string,unknown>)[field] : record[group!]);
      }
      el<HTMLTextAreaElement>('lighting-json').value = JSON.stringify(this.draft,null,2);
      el('lighting-source').textContent = `${override ? 'Browser override' : 'Room default'}: ${this.draft.name}`;
    }
    const button = el<HTMLButtonElement>('lighting-publish');
    button.hidden = this.local || !this.connection.state.permissions.canManage;
    button.disabled = this.busy || !room || (!override && !this.pending);
    button.textContent = this.pending ? 'Retry room publish' : 'Publish local look to room';
  }
  private async publish(): Promise<void> {
    const {room} = this.context();
    if (!room || this.busy || !this.connection.state.permissions.canManage) return;
    this.stopInput(); const context = this.contextKey;
    this.pending ??= {requestId:crypto.randomUUID(),kind:'room.update',expectedRevision:room.revision,settings:{...room.settings,lighting:structuredClone(this.draft)}};
    this.busy = true; this.refresh(); el('lighting-status').textContent = 'Publishing room lighting…';
    try {
      const response = await this.auth.post(`/api/rooms/${room.id}/commands`,this.pending);
      const body: unknown = await response.json(); if (context !== this.contextKey) return;
      const result = resultSchema.safeParse(body);
      if (!result.success) {
        const code = typeof body === 'object' && body !== null && 'error' in body ? String(body.error) : 'unknown outcome';
        if (['forbidden','invalid_settings','invalid_lighting','invalid_mutation'].includes(code)) this.pending = null;
        el('lighting-status').textContent = `Room lighting: ${code.replaceAll('_',' ')}. Retry to confirm an uncertain outcome.`; return;
      }
      this.pending = null;
      el('lighting-status').textContent = result.data.receipt.status === 'applied' ? 'Room lighting saved. Your browser override remains active until you choose Use room lighting.' : 'The room changed. Publish again to use its current revision.';
    } catch { if (context === this.contextKey) el('lighting-status').textContent = 'Outcome unknown. Retry room publish to confirm the same request.'; }
    finally { if (context === this.contextKey) { this.busy = false; this.refresh(); } }
  }
  dispose(): void { this.contextKey = ''; this.events.abort(); }
}
